import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const INVENTORY_HERO_PRODUCT_ASSET =
  "/assets/products/inventory/hero/itaas-inventory-product-dashboard.png";
export const INVENTORY_HERO_HUMAN_ASSET =
  "/assets/products/inventory/hero/itaas-inventory-warehouse-manager.png?v=1";

/** Editorial grounded in catalog SoT — not rewritten for this hero mission. */
export const INVENTORY_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "ops",
    icon: "🏭",
    titleFr: "Opérations",
    titleEn: "Operations",
    bodyFr: "Pilotez réceptions, sorties, transferts et niveaux de stock multi-entrepôts.",
    bodyEn: "Run receipts, shipments, transfers and multi-warehouse stock levels.",
  },
  {
    id: "logistics",
    icon: "📦",
    titleFr: "Logistique",
    titleEn: "Logistics",
    bodyFr: "Structurez entrepôts, zones et emplacements pour une traçabilité claire.",
    bodyEn: "Structure warehouses, zones and locations for clear traceability.",
  },
  {
    id: "supply",
    icon: "🔗",
    titleFr: "Supply chain",
    titleEn: "Supply chain",
    bodyFr: "Centralisez catalogue, mouvements et alertes de réapprovisionnement.",
    bodyEn: "Centralize catalog, movements and replenishment alerts.",
  },
  {
    id: "direction",
    icon: "🏛️",
    titleFr: "Direction",
    titleEn: "Leadership",
    bodyFr: "Suivez valeur du stock, alertes et activité depuis un tableau de bord unique.",
    bodyEn: "Track stock value, alerts and activity from one dashboard.",
  },
  {
    id: "sites",
    icon: "🗺️",
    titleFr: "Sites & magasins",
    titleEn: "Sites & stores",
    bodyFr: "Une source de vérité stock partagée pour retail, mines, santé et écoles.",
    bodyEn: "One shared stock truth for retail, mining, healthcare and schools.",
  },
];

export const INVENTORY_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "truth",
    icon: "📌",
    titleFr: "Source de vérité stock",
    titleEn: "Stock source of truth",
    bodyFr: "Un ledger partagé par les modules verticaux — sans duplication magasin par magasin.",
    bodyEn: "A ledger shared by vertical modules — without per-store duplication.",
  },
  {
    id: "ruptures",
    icon: "⚠️",
    titleFr: "Moins de ruptures",
    titleEn: "Fewer stockouts",
    bodyFr: "Alertes stock bas et réservations pour anticiper les manques.",
    bodyEn: "Low-stock alerts and reservations to anticipate shortages.",
  },
  {
    id: "terrain",
    icon: "📱",
    titleFr: "Opérations terrain plus rapides",
    titleEn: "Faster field operations",
    bodyFr: "Scan code-barres via AutoID pour accélérer réceptions et inventaires.",
    bodyEn: "Barcode scan via AutoID to speed receipts and inventory counts.",
  },
  {
    id: "suite",
    icon: "🧩",
    titleFr: "Intégré à la Suite",
    titleEn: "Suite-integrated",
    bodyFr: "Consommé par Hospitol, Edu, MinePortal et ERP sans doubles bases stock.",
    bodyEn: "Consumed by Hospitol, Edu, MinePortal and ERP without duplicate stock bases.",
  },
];

export const INVENTORY_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Stock & mouvements",
    titleEn: "Stock & movements",
    features: [
      "Moteur de stock multi-entrepôts (ledger)",
      "Réceptions, sorties, transferts et retours",
      "Réservations et alertes stock bas",
    ],
  },
  {
    titleFr: "Entrepôts & catalogue",
    titleEn: "Warehouses & catalog",
    features: [
      "Entrepôts, zones et emplacements",
      "Catalogue produits multi-magasins",
      "Réconciliation et rapports stock",
    ],
  },
  {
    titleFr: "Traçabilité",
    titleEn: "Traceability",
    features: [
      "Mouvements de stock par ledger immuable",
      "Scan code-barres via AutoID",
      "Multi-organisation natif",
    ],
  },
  {
    titleFr: "Intégrations",
    titleEn: "Integrations",
    features: [
      "Webhooks et intégrations sortantes",
      "Synchronisation catalogue avec MPM",
      "Consommé par toute la Suite ITaaS",
    ],
  },
];

const INVENTORY_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "suite",
    labelFr: "Moteur stock de la Suite ITaaS",
    labelEn: "ITaaS Suite stock engine",
  },
  {
    id: "multi",
    labelFr: "Multi-entrepôts et multi-organisation",
    labelEn: "Multi-warehouse and multi-organization",
  },
  {
    id: "local",
    labelFr: "Accompagnement ITaaS RDC",
    labelEn: "ITaaS RDC support",
  },
];

export const INVENTORY_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: INVENTORY_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS Inventory — stocks, entrepôts et mouvements",
    en: "ITaaS Inventory dashboard — stock, warehouses and movements",
  },
  humanImageSrc: INVENTORY_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Responsable logistique inspectant l’inventaire palette avec une tablette",
    en: "Warehouse manager inspecting pallet inventory with a tablet",
  },
  visualCaption: {
    fr: "Interface produit ITaaS Inventory — tableau de bord stock et supply chain",
    en: "ITaaS Inventory product interface — stock and supply chain dashboard",
  },
  heroKicker: {
    fr: "Stocks · Entrepôts · Mouvements · Traçabilité",
    en: "Stock · Warehouses · Movements · Traceability",
  },
  audiences: INVENTORY_AUDIENCES,
  audienceTitle: {
    fr: "Une supply chain claire pour chaque acteur opérationnel",
    en: "A clear supply chain for every operational stakeholder",
  },
  outcomes: INVENTORY_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour piloter stocks, mouvements et entrepôts.",
    en: "Practical benefits for running stock, movements and warehouses.",
  },
  capabilityGroups: INVENTORY_CAPABILITY_GROUPS,
  preferConfigCapabilities: true,
  contextTitle: {
    fr: "Moteur stock pour les opérations en Afrique centrale",
    en: "Stock engine for Central African operations",
  },
  contextLead: {
    fr: "Conçu pour retail, mines, santé et logistique — sans affirmation réglementaire non démontrée.",
    en: "Built for retail, mining, healthcare and logistics — without undemonstrated regulatory claims.",
  },
  contextFallbackPoints: [
    {
      fr: "Catalogue, magasins et ledger stock centralisés",
      en: "Centralized catalog, stores and stock ledger",
    },
    {
      fr: "Réceptions, sorties, transferts et alertes de réapprovisionnement",
      en: "Receipts, shipments, transfers and replenishment alerts",
    },
    {
      fr: "Source de vérité partagée avec les modules verticaux ITaaS",
      en: "Shared source of truth with ITaaS vertical modules",
    },
  ],
  trustBaseChips: INVENTORY_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
};
